"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, UserCheck, Clock, ArrowRight, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const pillars = [{
  title: "Transparent costing",
  description: "Detailed line-item scopes with defined material allowances before work begins.",
  icon: Compass
}, {
  title: "Dedicated project lead",
  description: "A single master remodeler supervising your Decatur home on site daily.",
  icon: UserCheck
}, {
  title: "Milestone-driven timelines",
  description: "Milestone-driven construction schedules with proactive weekly updates.",
  icon: Clock
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-28 pb-20 sm:pt-36 sm:pb-24 lg:pt-40 lg:pb-28 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mx-auto max-w-3xl text-center">
          {/* Eyebrow architectural badge */}
          <motion.div variants={itemVariants} className="flex justify-center mb-5">
            <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-sm">
              Methodology &amp; standards
            </Badge>
          </motion.div>

          {/* Main Display Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl leading-[1.15]">
            Our remodeling process &amp; planning guide
          </motion.h1>

          {/* Architectural 2px copper accent hairline divider */}
          <motion.div variants={itemVariants} className="mx-auto my-6 h-[2px] w-24 bg-primary" aria-hidden="true" />

          {/* Introductory Body Paragraph */}
          <motion.p variants={itemVariants} className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed font-sans">
            A calm, predictable approach to residential renovations across Decatur. From initial concept drawings and trade walk-throughs to fine finish carpentry, every phase is organized for clarity and craft.
          </motion.p>

          {/* Dual Action CTA Buttons */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-7 py-3 rounded-md shadow-sm hover:bg-primary/90 transition-[color,background-color,border-color,box-shadow]">
              <Link to="#process" className="inline-flex items-center gap-2">
                <span>Explore the 4-step process</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-card/90 text-foreground border-border font-semibold px-7 py-3 rounded-md shadow-2xs hover:bg-secondary transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <FileText className="size-4 text-muted-foreground" />
                <span>Request planning consultation</span>
              </Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Frosted Glass Key Pillars Strip */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-16 sm:mt-20 lg:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <div key={pillar.title} data-index={i} className="group relative rounded-xl border border-border bg-card/85 p-6 sm:p-8 backdrop-blur-md shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary border border-border">
                    <IconComponent className="size-5" />
                  </div>
                  <h2 className="font-serif text-lg sm:text-xl font-semibold text-card-foreground tracking-tight">
                    {pillar.title}
                  </h2>
                </div>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </div>;
        })}
        </motion.div>
      </div>
    </section>;
}